import { PlainDate } from "@kiss-solutions/plaindate";
import { v4 as uuid } from 'uuid';
import * as yup from "yup";
import { PROJEKT_BASE_URL } from "../api/projektApi";
import { i18_TRANSLATION } from "../i18nReferences";
import { convertStringToNumber } from "../store/utils/numericHelper";
import { transformNoNumberToUndefined } from "../store/utils/yupSchema";
import { getArtistsStdPreisFor } from "./artist";
import { toDateString } from "./date";

export const PROJEKTSTATUS_OFFEN = 0
export const PROJEKTSTATUS_RUHEND = 1
export const PROJEKTSTATUS_GESCHLOSSEN = 2
export const PROJEKTSTATUS_WARTEND = 3
export const PROJEKTSTATUS_OFFEN_OR_RUHEND = [PROJEKTSTATUS_OFFEN, PROJEKTSTATUS_RUHEND]
export const PROJEKTSTATUS_OFFEN_RUHEND_OR_WARTEND = [PROJEKTSTATUS_OFFEN, PROJEKTSTATUS_RUHEND, PROJEKTSTATUS_WARTEND]
export const PROEJKTSTATI_MAP = {
    PROJEKTSTATUS_OFFEN, PROJEKTSTATUS_RUHEND, PROJEKTSTATUS_GESCHLOSSEN, PROJEKTSTATUS_WARTEND
}
export const PROEJKTSTATI_TS_MAP = Object.keys(PROEJKTSTATI_MAP).reduce((result, key) => ({
    ...result,
    [PROEJKTSTATI_MAP[key]]: key
}), {})

export const getTranslatedProjektStatus = (t, projektStatus) => {
    return t(PROEJKTSTATI_TS_MAP[projektStatus], { ns: i18_TRANSLATION })
}

export const convertImageKeyToUrl = (key) => {
    const encodedKey = encodeURIComponent(key)
    return `${PROJEKT_BASE_URL}/image?key=${encodedKey}`
}

export const convertProjekt2SelectionTitleWithArtist = (projekt, translatedProjektstatusMap, artistMap) => `${artistMap[projekt.ArtistID].Kuerzel}: ${projekt.ProjName} (${translatedProjektstatusMap[projekt.ProjStat]})`

export const convertProjekt2SelectionWithArtist = (projekt, translatedProjektstatusMap, artistMap) => ({
    id: projekt.ProjektID,
    text: `${artistMap[projekt.ArtistID].Kuerzel}: ${projekt.ProjName} (${translatedProjektstatusMap[projekt.ProjStat]})`,
    value: projekt
})

/** Merges appointment-shaped `projekt` form values into a persisted project entity. */
export const mergeAppointmentProjektFormWithProjekt = (projektFormData, projekt, loggedInArtistId) =>
    projekt &&
    projektFormData && {
        ...projekt,
        ArtistID: projektFormData.ArtistID,
        ProjName: projektFormData.ProjName,
        ProjStat: projektFormData.ProjStat,
        Bemerkung: projektFormData.Bemerkung,
        Preis: projektFormData.Preis,
        AnzInfo: projektFormData.AnzInfo,
        ...convertAppointmentProjektSchema2Projekt(projektFormData),
        _createdID: projekt._createdID || loggedInArtistId,
        _createdTS: projekt._createdTS || new Date().toISOString(),
    };

// Converter for nested projekt form shape (AppointmentPage, EditProjektDialog)
export const convertAppointmentProjektSchema2Projekt = (projektFormData) => ({
    ...projektFormData
    , AnzAgreed: projektFormData.AnzAgreed && +projektFormData.AnzAgreed || 0
    , AnzStatus: projektFormData.AnzStatus && +projektFormData.AnzStatus || 0
    , WarteVon: projektFormData.WarteVon && toDateString(projektFormData.WarteVon) || null
    , WarteBis: projektFormData.WarteBis && toDateString(projektFormData.WarteBis) || null
    , AnzTerm: (projektFormData.Offen || 0) + (projektFormData.Erledigt || 0)
})

// Converter für Backend-Daten zu verschachtelter projekt.* Schema-Struktur
export const convertProjekt2AppointmentSchema = (projekt) => {
    if (!projekt) return {};
   
    return {
        ProjektID: projekt.ProjektID,
        KundID: projekt.KundID,
        ArtistID: projekt.ArtistID,
        ProjName: projekt.ProjName || '',
        ProjStat: projekt.ProjStat,
        Bemerkung: projekt.Bemerkung || '',
        Preis: projekt.Preis || '',
        AnzAgreed: projekt.AnzAgreed || '0',
        AnzStatus: projekt.AnzStatus || '0',
        AnzInfo: projekt.AnzInfo || '',
        Offen: projekt.Offen || 0,
        Erledigt: projekt.Erledigt || 0,
        AnzTerm: projekt.AnzTerm || 0,
        AttFolder: projekt.AttFolder || uuid(),
        LetzTerm: projekt.LetzTerm && new Date(projekt.LetzTerm) || undefined,
        LetzArbTerm: projekt.LetzArbTerm && new Date(projekt.LetzArbTerm) || undefined,
        WarteVon: projekt.WarteVon && new PlainDate(projekt.WarteVon).toLocalDate() || undefined,
        WarteBis: projekt.WarteBis && new PlainDate(projekt.WarteBis).toLocalDate() || undefined,
        _createdID: projekt._createdID,
        _createdTS: projekt._createdTS
    }
}

export const hasVereinbarteAnzahlung = (anzAgreed) => convertStringToNumber(anzAgreed, 0) > 0

/** Aligns with finalizer: only artists with ManageAZ get deposit status support. */
export const isArtistManagingAnzahlung = (manageAZ) => manageAZ === true || manageAZ === 1

export const getProjektArtistManageAZ = (artistId, artistMap) => {
    if (artistId == null || !artistMap) {
        return false
    }
    return isArtistManagingAnzahlung(artistMap[artistId]?.ManageAZ)
}

export const shouldValidateProjektAnzahlungFields = ({artistId, artistMap, anzAgreed}) =>
    getProjektArtistManageAZ(artistId, artistMap) && hasVereinbarteAnzahlung(anzAgreed)

const skipAnzahlungFieldValidation = (schema) => schema.nullable(true).optional()

const applyAnzStatusValidationRules = ([AnzAgreed, ProjStat, ProjektID], schema) => {
    if (!hasVereinbarteAnzahlung(AnzAgreed)) {
        return skipAnzahlungFieldValidation(schema)
    }
    if (ProjStat === PROJEKTSTATUS_OFFEN && isGespeichertesProjekt(ProjektID) && AnzAgreed > 0 && ProjStat === PROJEKTSTATUS_OFFEN) {
        return schema.min(AnzAgreed, {key: 'YUP.WARNING.ANZSSTATUS_NOT_FULL'}).required({key: 'YUP.WARNING.ANZSSTATUS_NOT_FULL'})
            .max(AnzAgreed, ({max}) => ({key: 'YUP.WARNING.LESS_THAN', values: {max}}))
    }
    if (ProjStat === PROJEKTSTATUS_GESCHLOSSEN) {
        return schema.max(0, {key: 'YUP.WARNING.ANZSSTATUS_SHOULD_BE_EMPTY'}).nullable(true)
    }
    return schema.optional().max(AnzAgreed, ({max}) => ({key: 'YUP.WARNING.LESS_THAN', values: {max}}))
}

const getValidationForAnzInfo = ([AnzAgreed, artistId, artistMap], schema) => {
    if (!getProjektArtistManageAZ(artistId, artistMap)) {
        return skipAnzahlungFieldValidation(schema)
    }
    return hasVereinbarteAnzahlung(AnzAgreed)
        ? schema.nullable(true)
        : schema.required({key: 'YUP.WARNING.NEED_ANZINFO'}).nullable(false)
}

const getValidationForAnzStatus = ([AnzAgreed, ProjStat, ProjektID, artistId, artistMap], schema) => {
    if (!getProjektArtistManageAZ(artistId, artistMap)) {
        return skipAnzahlungFieldValidation(schema)
    }
    return applyAnzStatusValidationRules([AnzAgreed, ProjStat, ProjektID], schema)
}

const createWarteVonSchema = ([WarteBis, ProjStat], schema) => {
    if (WarteBis) {
        return schema.required().max(WarteBis, {key: 'YUP.ERROR.START_AFTER_END'})
    }
    if (ProjStat === PROJEKTSTATUS_WARTEND) {
        return schema.required()
    }
    schema.nullable(true)
}

export const createProjekt = ({ProjName, artist, KundID, _createdID, anzInfoText}) => ({
    ProjektID: -1,
    ProjName,
    ArtistID: artist?.ArtistID,
    ProjStat: PROJEKTSTATUS_OFFEN,
    KundID,
    AnzAgreed: artist?.Anzahlung,
    AnzStatus: 0,
    AnzInfo: !artist?.Anzahlung ? anzInfoText : undefined,
    AttFolder: uuid(),
    AnzTerm: 1,
    Preis: getArtistsStdPreisFor(artist, 1),
    Offen: 1,
    Erledigt: 0,
    LetzTerm: null,
    LetzArbTerm: null,
    _createdID,
    _createdTS: new Date().toISOString()
})


export const validateProjekt = (projekt, compareDate = new Date()) => {
    const errors = {}
    const anzAgreed = projekt.AnzAgreed && +projekt.AnzAgreed
    const anzStatus = projekt.AnzStatus && +projekt.AnzStatus
    if (anzAgreed < 0 || !anzAgreed && !projekt.AnzInfo) {
        errors.AnzAgreed = 1
    }
    if (projekt.WarteBis === undefined
        && projekt.ProjStat !== PROJEKTSTATUS_WARTEND
        && projekt.WarteVon !== undefined) {
        errors.WarteBis = 1
    }
    if (projekt.WarteVon === undefined
        && (projekt.ProjStat === PROJEKTSTATUS_WARTEND || projekt.WarteBis !== undefined)) {
        errors.WarteVon = 1
    }
    if (anzAgreed && anzAgreed > anzStatus && projekt.ProjStat === PROJEKTSTATUS_OFFEN
        || anzStatus && projekt.ProjStat === PROJEKTSTATUS_GESCHLOSSEN) {
        errors.AnzStatus = 1
    }
    if ((!projekt.Preis || projekt.Preis.length < 1)
        && ![PROJEKTSTATUS_WARTEND, PROJEKTSTATUS_GESCHLOSSEN].includes(projekt.ProjStat)) {
        errors.Preis = 1
    }
    if (projekt.ProjStat === PROJEKTSTATUS_GESCHLOSSEN && new Date(projekt.LetzTerm).getTime() > compareDate.getTime()) {
        errors.LetzTerm = 1
    }
    if (projekt.gesperrt) {
        errors.gesperrt = 1
    }
    if (projekt.hatAnzDifferenz) {
        errors.hatAnzDifferenz = 1
    }
    return errors
}

export const isGespeichertesProjekt = projektId => projektId > 0

export const checkForWarteDateChanges = (projekt, projektStatus, WarteVon) => {
    if (projektStatus === PROJEKTSTATUS_WARTEND
        && !projekt.WarteVon) {
        return {WarteVon: new Date()}
    }
    if (isGespeichertesProjekt(projekt.ProjektID)
        && (projekt.ProjStat === PROJEKTSTATUS_WARTEND || WarteVon)
        && projektStatus !== PROJEKTSTATUS_WARTEND
        && !projekt.WarteBis) {
        return {WarteBis: new Date()}
    } else if (!isGespeichertesProjekt(projekt.ProjektID)
        && projektStatus !== PROJEKTSTATUS_WARTEND) {
        return {
            WarteBis: undefined,
            WarteVon: undefined
        }
    }
    return {}
}

export const fehltAnzahlungKomplett = projekt => projekt && convertStringToNumber(projekt.AnzStatus, 0) <= 0 && convertStringToNumber(projekt.AnzAgreed, 0) > 0 || false

export const getLetzterTermin = projekt => projekt.ProjStat === PROJEKTSTATUS_GESCHLOSSEN ? projekt.LetzTerm : projekt.LetzArbTerm

export const convertImagesRaw2ProjektImages = images => images.map(t => ({type: 'projekt', ...t}))

export const convertProjName2ProjNameWithArtist = (artistMap, artistId, ProjName) => artistId && `${artistMap[artistId]?.Kuerzel}: ${ProjName}`

export const canCompressProjekte = (projektList, canEditProjektHandler) => {
    if (projektList?.length < 2) {
        return false
    }
    const kundenArtistCominations = projektList.reduce((pv, cv) => {
        const id = `${cv.KundID}#${cv.ArtistID}`
        if (!pv.includes(id)) {
            pv.push(id)
        }
        return pv
    }, [])
    if (kundenArtistCominations.length !== 1) {
        return false
    }
    return !(canEditProjektHandler && !canEditProjektHandler(projektList[0]));

}

export const getTranslatedProjektStatusMap = (t) => Object.keys(PROEJKTSTATI_MAP).reduce((pv, cv) => {
    pv[PROEJKTSTATI_MAP[cv]] = t(cv, {ns: i18_TRANSLATION})
    return pv
}, {})

// Nested `projekt` schema for AppointmentPage and EditProjektDialog (shared UI via ProjectForm)
export const createEditProjektDialogSchema = () =>
    yup.object({
        projekt: createAppointmentProjektSchema(),
    });

export const createAppointmentProjektSchema = () => {
    return yup.object().shape({
        ProjektID: yup.number().nullable(true),
        ArtistID: yup.number().required(),
        ProjName: yup.string().max(30).required(),
        ProjStat: yup.number().nullable(true),
        AnzAgreed: yup.number().transform(transformNoNumberToUndefined).min(0).nullable(true),
        AnzInfo: yup.string().max(30).when(['AnzAgreed', 'ArtistID', '$artistMap'], getValidationForAnzInfo),
        AnzStatus: yup.number().transform(transformNoNumberToUndefined).min(0)
            .when(['AnzAgreed', 'ProjStat', 'ProjektID', 'ArtistID', '$artistMap'], getValidationForAnzStatus),
        Preis: yup.string().max(100).when(['ProjStat'], ([ProjStat], schema) => 
            PROJEKTSTATUS_OFFEN_OR_RUHEND.includes(ProjStat) ? schema.required({key: 'YUP.WARNING.NEED'}) : schema.nullable(true)
        ),
        LetzTerm: yup.date().when(['ProjStat', 'ProjektID'],
            ([ProjStat, ProjektID], schema) => 
                ProjStat === PROJEKTSTATUS_OFFEN && ProjektID > 0 ? 
                    schema.required({key: 'YUP.WARNING.NO_APPOINTMENTS'}).min(new Date(), {key: 'YUP.WARNING.NEED_APPOINTMENTS'}) : 
                    schema.optional()
        ),
        LetzArbTerm: yup.date().when(['ProjStat'],
            ([ProjStat], schema) => ProjStat === PROJEKTSTATUS_GESCHLOSSEN ?
                schema.max(new Date(), () => ({key: 'YUP.WARNING.CLOSED_PROJEKT_WITH_FUTURE_APPOINTMENTS'})) : 
                schema.optional()
        ),
        WarteBis: yup.date().nullable(true),
        WarteVon: yup.date().when(['WarteBis', 'ProjStat'], createWarteVonSchema),
        Bemerkung: yup.string().nullable(true),
        Offen: yup.number().nullable(true),
        Erledigt: yup.number().nullable(true),
        AnzTerm: yup.number().nullable(true)
    });
};
